import React, { useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { Styles } from './styles';
import {
  FlipDirection,
  RotationDegrees,
  WatermarkPosition
} from '@utils/image';

const WATERMARK_POSITIONS: { value: WatermarkPosition; label: string }[] = [
  { value: 'top-left', label: '↖ Sup. Esq.' },
  { value: 'top-right', label: '↗ Sup. Dir.' },
  { value: 'center', label: '⊕ Centro' },
  { value: 'bottom-left', label: '↙ Inf. Esq.' },
  { value: 'bottom-right', label: '↘ Inf. Dir.' }
];

const SHOW_WATERMARK_TOOLS = false;

export interface ImageModalProps {
  images: string[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (index: number) => void;
  /** IDs das fotos na mesma ordem de `images` */
  photoIds?: number[];
  /** Posições de marca d'água ativas por photo ID */
  watermarkPositions?: Map<number, WatermarkPosition>;
  /** Opacidades de marca d'água por photo ID (0-1) */
  watermarkOpacities?: Map<number, number>;
  /** Tamanhos de marca d'água por photo ID (0-1, fracção da largura) */
  watermarkSizes?: Map<number, number>;
  /** Callback para mudar posição da marca d'água */
  onWatermarkPositionChange?: (
    photoId: number,
    position: WatermarkPosition
  ) => Promise<void>;
  /** Callback para mudar opacidade da marca d'água */
  onWatermarkOpacityChange?: (
    photoId: number,
    opacity: number
  ) => Promise<void>;
  /** Callback para mudar tamanho da marca d'água */
  onWatermarkSizeChange?: (
    photoId: number,
    relativeWidth: number
  ) => Promise<void>;
  /** Callback para girar a imagem (aplica/re-aplica marca d'água) */
  onRotateImage?: (photoId: number, degrees: RotationDegrees) => Promise<void>;
  /** Callback para espelhar a imagem (aplica/re-aplica marca d'água) */
  onFlipImage?: (photoId: number, direction: FlipDirection) => Promise<void>;
  /** Legendas das fotos, na mesma ordem de `images` */
  captions?: string[];
  /** Callback para alterar a legenda de uma foto */
  onCaptionChange?: (index: number, caption: string) => void;
}

export const ImageModal: React.FC<ImageModalProps> = ({
  images,
  currentIndex,
  isOpen,
  onClose,
  onNavigate,
  photoIds,
  watermarkPositions,
  watermarkOpacities,
  watermarkSizes,
  onWatermarkPositionChange,
  onWatermarkOpacityChange,
  onWatermarkSizeChange,
  onRotateImage,
  onFlipImage,
  captions,
  onCaptionChange
}) => {
  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      onNavigate(currentIndex - 1);
    }
  }, [currentIndex, onNavigate]);

  const handleNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      onNavigate(currentIndex + 1);
    }
  }, [currentIndex, images.length, onNavigate]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
      }
    },
    [isOpen, onClose, handlePrevious, handleNext]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Valores locais dos sliders — atualizam o display imediatamente enquanto
  // o utilizador arrasta, sem disparar o callback pesado a cada tick.
  const [draftOpacity, setDraftOpacity] = useState<number>(0.7);
  const [draftSize, setDraftSize] = useState<number>(0.25);

  // Sincroniza os drafts sempre que a foto exibida muda ou os valores externos mudam
  useEffect(() => {
    const photoId = photoIds?.[currentIndex];
    setDraftOpacity(
      photoId !== undefined ? (watermarkOpacities?.get(photoId) ?? 0.7) : 0.7
    );
    setDraftSize(
      photoId !== undefined ? (watermarkSizes?.get(photoId) ?? 0.25) : 0.25
    );
  }, [currentIndex, photoIds, watermarkOpacities, watermarkSizes]);

  if (!isOpen || images.length === 0) return null;

  const currentImage = images[currentIndex];
  const currentPhotoId = photoIds?.[currentIndex];
  const isNewPhoto =
    currentPhotoId !== undefined && watermarkPositions?.has(currentPhotoId);
  const hasTransformTools = Boolean(onRotateImage || onFlipImage);
  const shouldShowToolbar =
    currentPhotoId !== undefined &&
    isNewPhoto &&
    (hasTransformTools || SHOW_WATERMARK_TOOLS);
  const currentWatermarkPosition =
    currentPhotoId !== undefined
      ? (watermarkPositions?.get(currentPhotoId) ?? 'bottom-right')
      : 'bottom-right';

  return createPortal(
    <Styles.Overlay onClick={onClose}>
      <Styles.CloseButton onClick={onClose} aria-label="Fechar">
        ×
      </Styles.CloseButton>

      {/* Toolbar: transformações + marca d'água — apenas para fotos novas */}
      {shouldShowToolbar && (
        <Styles.WatermarkToolbar onClick={(e) => e.stopPropagation()}>
          {/* Linha 0: girar / espelhar */}
          {hasTransformTools && (
            <Styles.WatermarkToolbarRow>
              <Styles.WatermarkLabel>Transformar:</Styles.WatermarkLabel>
              {onRotateImage && (
                <>
                  <Styles.WatermarkButton
                    $active={false}
                    title="Girar 90° anti-horário"
                    onClick={() => onRotateImage(currentPhotoId, -90)}>
                    ↺ 90°
                  </Styles.WatermarkButton>
                  <Styles.WatermarkButton
                    $active={false}
                    title="Girar 90° horário"
                    onClick={() => onRotateImage(currentPhotoId, 90)}>
                    ↻ 90°
                  </Styles.WatermarkButton>
                  <Styles.WatermarkButton
                    $active={false}
                    title="Girar 180°"
                    onClick={() => onRotateImage(currentPhotoId, 180)}>
                    ↕ 180°
                  </Styles.WatermarkButton>
                </>
              )}
              {onFlipImage && (
                <>
                  <Styles.WatermarkButton
                    $active={false}
                    title="Espelhar horizontalmente"
                    onClick={() => onFlipImage(currentPhotoId, 'horizontal')}>
                    ⇔ Horiz.
                  </Styles.WatermarkButton>
                  <Styles.WatermarkButton
                    $active={false}
                    title="Espelhar verticalmente"
                    onClick={() => onFlipImage(currentPhotoId, 'vertical')}>
                    ⇕ Vert.
                  </Styles.WatermarkButton>
                </>
              )}
            </Styles.WatermarkToolbarRow>
          )}

          {/* Separador entre transformações e controles de marca d'água */}
          {SHOW_WATERMARK_TOOLS && (
            <>
              {hasTransformTools && <Styles.WatermarkDivider />}

              {/* Linhas 1–3: marca d'água */}
              <Styles.WatermarkToolbarRow>
                <Styles.WatermarkLabel>Posição:</Styles.WatermarkLabel>
                {WATERMARK_POSITIONS.map(({ value, label }) => (
                  <Styles.WatermarkButton
                    key={value}
                    $active={currentWatermarkPosition === value}
                    onClick={() =>
                      onWatermarkPositionChange?.(currentPhotoId, value)
                    }>
                    {label}
                  </Styles.WatermarkButton>
                ))}
              </Styles.WatermarkToolbarRow>

              <Styles.WatermarkToolbarRow>
                <Styles.WatermarkSliderLabel>
                  Opacidade:
                </Styles.WatermarkSliderLabel>
                <Styles.WatermarkSlider
                  type="range"
                  min={0.1}
                  max={1}
                  step={0.05}
                  value={draftOpacity}
                  onChange={(e) => setDraftOpacity(parseFloat(e.target.value))}
                  onPointerUp={(e) =>
                    onWatermarkOpacityChange?.(
                      currentPhotoId,
                      parseFloat((e.target as HTMLInputElement).value)
                    )
                  }
                />
                <Styles.WatermarkSliderValue>
                  {Math.round(draftOpacity * 100)}%
                </Styles.WatermarkSliderValue>
              </Styles.WatermarkToolbarRow>

              <Styles.WatermarkToolbarRow>
                <Styles.WatermarkSliderLabel>
                  Tamanho:
                </Styles.WatermarkSliderLabel>
                <Styles.WatermarkSlider
                  type="range"
                  min={0.05}
                  max={0.6}
                  step={0.05}
                  value={draftSize}
                  onChange={(e) => setDraftSize(parseFloat(e.target.value))}
                  onPointerUp={(e) =>
                    onWatermarkSizeChange?.(
                      currentPhotoId,
                      parseFloat((e.target as HTMLInputElement).value)
                    )
                  }
                />
                <Styles.WatermarkSliderValue>
                  {Math.round(draftSize * 100)}%
                </Styles.WatermarkSliderValue>
              </Styles.WatermarkToolbarRow>
            </>
          )}
        </Styles.WatermarkToolbar>
      )}

      <Styles.NavButton
        $position="left"
        onClick={(e) => {
          e.stopPropagation();
          handlePrevious();
        }}
        disabled={currentIndex === 0}
        aria-label="Imagem anterior">
        ‹
      </Styles.NavButton>

      <Styles.ModalContent onClick={(e) => e.stopPropagation()}>
        <Styles.ModalImage
          src={currentImage}
          alt={captions?.[currentIndex] || `Imagem ${currentIndex + 1}`}
        />
        {onCaptionChange && (
          <Styles.CaptionContainer>
            <Styles.CaptionLabel>Legenda:</Styles.CaptionLabel>
            <Styles.CaptionInput
              type="text"
              placeholder="Adicione uma legenda para esta foto..."
              value={captions?.[currentIndex] ?? ''}
              onChange={(e) => onCaptionChange(currentIndex, e.target.value)}
            />
          </Styles.CaptionContainer>
        )}
      </Styles.ModalContent>

      <Styles.NavButton
        $position="right"
        onClick={(e) => {
          e.stopPropagation();
          handleNext();
        }}
        disabled={currentIndex === images.length - 1}
        aria-label="Próxima imagem">
        ›
      </Styles.NavButton>

      <Styles.ImageCounter>
        {currentIndex + 1} / {images.length}
      </Styles.ImageCounter>
    </Styles.Overlay>,
    document.body
  );
};

export default ImageModal;
